import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Building2, QrCode, CheckCircle2, Link2 } from 'lucide-react';
import { ProgressSidebar } from './ProgressSidebar';
import { StepConnect } from './StepConnect';
import { StepBusinessInfo } from './StepBusinessInfo';
import { StepConnectionMethod } from './StepConnectionMethod';
import { StepScanQR } from './StepScanQR';
import { StepPairingCode } from './StepPairingCode';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
const STEPS = [{
  id: 1,
  label: 'Connect Phone',
  icon: Smartphone
}, {
  id: 2,
  label: 'Business Info',
  icon: Building2
}, {
  id: 3,
  label: 'Connection Method',
  icon: Link2
}, {
  id: 4,
  label: 'Connect WhatsApp',
  icon: QrCode
}];
interface OnboardingData {
  phone: string;
  businessName: string;
  category: string;
  description: string;
}
type ConnectionMethod = 'qr' | 'pairing' | null;
export function OnboardingFlow() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [connectionMethod, setConnectionMethod] = useState<ConnectionMethod>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    phone: '',
    businessName: '',
    category: '',
    description: ''
  });
  const navigate = useNavigate();
  const {
    updateUser
  } = useAuth();
  const {
    t
  } = useLanguage();
  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  const prevStep = () => {
    // If going back from step 4, reset connection method
    if (currentStep === 4) {
      setConnectionMethod(null);
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const finish = () => {
    console.log('=== FINISHING ONBOARDING ===');
    console.log('Business Name (will be instanceName):', onboardingData.businessName);
    console.log('Full onboarding data:', onboardingData);
    updateUser({
      instanceName: onboardingData.businessName,
      systemMessage: undefined
    });
    console.log('User updated with instanceName:', onboardingData.businessName);
    setIsComplete(true);
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };
  const handleStepData = (data: Partial<OnboardingData>) => {
    console.log('Updating onboarding data:', data);
    setOnboardingData(prev => {
      const updated = {
        ...prev,
        ...data
      };
      console.log('Updated onboarding data:', updated);
      return updated;
    });
  };
  const handleSelectQR = () => {
    setConnectionMethod('qr');
    nextStep();
  };
  const handleSelectPairingCode = () => {
    setConnectionMethod('pairing');
    nextStep();
  };
  if (isComplete) {
    return <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#4f1964] via-[#6d28d9] to-[#7c3aed] relative overflow-hidden" data-id="element-423">
        {/* Animated Background Circles */}
        <motion.div className="absolute inset-0" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} data-id="element-424">
          <motion.div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-425" />
          <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-300/10 rounded-full blur-3xl" animate={{
          scale: [1, 1.3, 1],
          x: [0, -50, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-426" />
        </motion.div>

        {/* Celebration Emojis */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-427">
          {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute text-4xl" initial={{
          x: `${Math.random() * 100}vw`,
          y: -50,
          opacity: 0,
          rotate: 0
        }} animate={{
          y: '110vh',
          opacity: [0, 1, 1, 0],
          rotate: 360
        }} transition={{
          duration: 3 + Math.random() * 2,
          delay: Math.random() * 0.5,
          ease: 'linear'
        }} data-id="element-428">
              {['🎉', '✨', '🎊', '⭐', '💫', '🌟'][Math.floor(Math.random() * 6)]}
            </motion.div>)}
        </div>

        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        duration: 0.6
      }} className="text-center text-white relative z-10" data-id="element-429">
          <motion.div initial={{
          scale: 0,
          rotate: -180
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200
        }} className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white/30 shadow-2xl" data-id="element-430">
            <motion.div animate={{
            scale: [1, 1.2, 1]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-431">
              <CheckCircle2 className="w-16 h-16" data-id="element-432" />
            </motion.div>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-4xl font-bold mb-3" data-id="element-433">
            {t('onboarding.qr.connected')} 🎉
          </motion.h2>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} className="text-purple-100 text-xl" data-id="element-434">
            {t('common.loading')}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.8
        }} className="mt-6 flex items-center justify-center gap-2" data-id="element-435">
            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full" data-id="element-436" />
          </motion.div>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col md:flex-row bg-gray-50 overflow-hidden" data-id="element-437">
      {/* Sidebar Area */}
      <div className="w-full md:w-[40%] bg-gradient-to-br from-[#4f1964] via-[#6d28d9] to-[#7c3aed] relative" data-id="element-438">
        <ProgressSidebar currentStep={currentStep} steps={STEPS} data-id="element-439" />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 relative flex flex-col items-center justify-center p-4 md:p-8 lg:p-12 overflow-y-auto" data-id="element-440">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 bg-gray-50 -z-10" data-id="element-441" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-442" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-443" />

        <motion.div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8 md:p-10 relative z-10" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-444">
          <AnimatePresence mode="wait" data-id="element-445">
            <motion.div key={`${currentStep}-${connectionMethod}`} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="w-full" data-id="element-446">
              {currentStep === 1 && <StepConnect onNext={phone => {
              handleStepData({
                phone
              });
              nextStep();
            }} data-id="element-447" />}
              {currentStep === 2 && <StepBusinessInfo onNext={businessData => {
              console.log('Business data received from StepBusinessInfo:', businessData);
              handleStepData(businessData);
              nextStep();
            }} onBack={prevStep} data-id="element-448" />}
              {currentStep === 3 && <StepConnectionMethod onSelectQR={handleSelectQR} onSelectPairingCode={handleSelectPairingCode} onBack={prevStep} data-id="element-449" />}
              {currentStep === 4 && connectionMethod === 'qr' && <StepScanQR instanceName={onboardingData.businessName} phone={onboardingData.phone} onFinish={finish} onBack={prevStep} data-id="element-450" />}
              {currentStep === 4 && connectionMethod === 'pairing' && <StepPairingCode instanceName={onboardingData.businessName} phone={onboardingData.phone} onFinish={finish} onBack={prevStep} data-id="element-451" />}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>;
}